import { useEffect, useState } from "react";
import { Plus, ShieldCheck, UserCog } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Card, EmptyState, InlineNotice, PageHeader, StatusBadge } from "../components/ui";

interface UserItem {
  id: string;
  name: string;
  email: string;
  role: "OWNER" | "MANAGER" | "STAFF" | "ACCOUNTANT";
  isActive: boolean;
  createdAt: string;
}

const roleLabels: Record<UserItem["role"], string> = {
  OWNER: "Chủ cửa hàng",
  MANAGER: "Quản lý",
  STAFF: "Nhân viên",
  ACCOUNTANT: "Kế toán",
};

export function UsersPage() {
  const auth = useAuth();
  const [users, setUsers] = useState<UserItem[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "STAFF" as "MANAGER" | "STAFF" | "ACCOUNTANT" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const canView = ["OWNER", "MANAGER"].includes(auth.session?.user.role ?? "");
  const canCreate = auth.session?.user.role === "OWNER" && navigator.onLine && !auth.demo;

  async function load() {
    if (!canView || auth.demo || !navigator.onLine) return;
    try {
      setUsers(await api<UserItem[]>("/users"));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Không thể tải danh sách người dùng");
    }
  }

  useEffect(() => { void load(); }, [auth.demo, auth.session?.user.role]);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!canCreate) return;
    setBusy(true);
    setError("");
    try {
      await api("/users", { method: "POST", body: JSON.stringify(form) });
      setForm({ name: "", email: "", password: "", role: "STAFF" });
      setShowForm(false);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Không thể tạo người dùng");
    } finally {
      setBusy(false);
    }
  }

  if (!canView) {
    return (
      <div className="page-stack">
        <PageHeader back title="Người dùng & phân quyền" />
        <InlineNotice tone="danger" title="Không có quyền">Chỉ Chủ cửa hàng hoặc Quản lý được xem cấu hình người dùng.</InlineNotice>
      </div>
    );
  }

  return (
    <div className="page-stack">
      <PageHeader
        title="Người dùng & phân quyền"
        description="Phân quyền ở server; ẩn nút trên UI không được xem là cơ chế bảo mật"
        action={canCreate ? <button className="btn btn-primary" onClick={() => setShowForm(true)}><Plus size={18} /> Người dùng</button> : undefined}
      />
      {error ? <InlineNotice tone="danger" title={error} /> : null}
      {!navigator.onLine && !auth.demo ? <InlineNotice tone="warning" title="Cần kết nối mạng">Danh sách quyền phải được lấy từ server authoritative.</InlineNotice> : null}

      {users.length === 0 && !auth.demo ? (
        <EmptyState title="Chưa tải được danh sách người dùng" description="Kết nối mạng hoặc thử lại sau." />
      ) : auth.demo ? (
        <Card className="user-list">
          <div className="user-row"><span className="partner-icon"><ShieldCheck size={20} /></span><div><strong>Chủ cửa hàng Demo</strong><small>demo@local · Toàn quyền</small></div><StatusBadge tone="success">Chủ cửa hàng</StatusBadge></div>
        </Card>
      ) : (
        <Card className="user-list">
          {users.map((user) => (
            <div className="user-row" key={user.id}>
              <span className="partner-icon"><UserCog size={20} /></span>
              <div><strong>{user.name}</strong><small>{user.email}</small></div>
              <StatusBadge tone={user.isActive ? "success" : "neutral"}>{roleLabels[user.role]}</StatusBadge>
            </div>
          ))}
        </Card>
      )}

      <Card className="permission-matrix">
        <h2>Vai trò mặc định</h2>
        <div><strong>Chủ cửa hàng</strong><span>Toàn quyền, người dùng, cấu hình, dữ liệu tài chính.</span></div>
        <div><strong>Quản lý</strong><span>Bán hàng, kho, báo cáo, công nợ và kiểm soát vận hành.</span></div>
        <div><strong>Nhân viên</strong><span>Bán hàng và thao tác nghiệp vụ được cấp; không tự sửa tồn/đóng sổ.</span></div>
        <div><strong>Kế toán</strong><span>Báo cáo, chứng từ, hóa đơn và dữ liệu thuế.</span></div>
      </Card>

      {showForm ? (
        <div className="modal-backdrop" onMouseDown={() => !busy && setShowForm(false)}>
          <form className="bottom-sheet" onSubmit={submit} onMouseDown={(event) => event.stopPropagation()}>
            <div className="sheet-handle" />
            <div className="form-section-title"><UserCog size={20} /><h2>Thêm người dùng</h2></div>
            <label>Họ tên<input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} required /></label>
            <label>Email<input type="email" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} required /></label>
            <label>Mật khẩu tạm<input type="password" minLength={10} value={form.password} onChange={(event) => setForm({ ...form, password: event.target.value })} required /></label>
            <label>Vai trò
              <select value={form.role} onChange={(event) => setForm({ ...form, role: event.target.value as typeof form.role })}>
                <option value="STAFF">Nhân viên</option>
                <option value="MANAGER">Quản lý</option>
                <option value="ACCOUNTANT">Kế toán</option>
              </select>
            </label>
            <InlineNotice tone="info" title="Nguyên tắc least privilege">Chỉ cấp quyền đủ cho công việc. Quyền được kiểm tra lại tại API.</InlineNotice>
            <button className="btn btn-primary btn-lg" disabled={busy}>{busy ? "Đang tạo..." : "Tạo người dùng"}</button>
          </form>
        </div>
      ) : null}
    </div>
  );
}
